import apiClient from './apiClient';
import strategyTransformationService, { FrontendStrategyData, BackendStrategyData } from './strategyTransformationService';
import { ApiResponse, StrategyMetadata, StrategyWithLegs } from '../types';
import { transformStrategyFields } from '../utils/transformStrategyFields';
import { withActingAsIfSuperAdmin } from './actingAs';

// All strategy endpoints use the 'strategy' API
const API_TYPE = 'api' as const;

/** Append ?acting_as=<id> ONLY when caller is SuperAdmin. Backend enforces
 *  super_admin_only=True on every method (GET reads + mutations alike); non-
 *  SuperAdmin callers see own data silently rather than hitting 403. Applied
 *  to mutations so SuperAdmin-on-behalf edits land in the target's partition. */
function withQs(path: string): string {
  const qs = withActingAsIfSuperAdmin(new URLSearchParams()).toString();
  return qs ? `${path}?${qs}` : path;
}

interface StrategyCreationResponse {
  success: boolean;
  data: any;
  message: string;
  warnings?: string[];
}

interface StrategyExecutionStatus {
  status: 'ACTIVE' | 'PAUSED' | 'INACTIVE' | 'COMPLETED' | 'ERROR';
  lastExecution?: string;
  nextExecution?: string;
  executionCount: number;
  performance?: {
    totalReturn: number;
    successRate: number;
    averageReturn: number;
  };
}

class StrategyService {
  /**
   * Create a new strategy in a basket using the transformation service
   */
  async createStrategy(basketId: string, strategyData: FrontendStrategyData): Promise<StrategyCreationResponse> {
    try {
      // Perform advanced validation first
      const validation = await strategyTransformationService.validateFrontendDataAdvanced(strategyData);

      if (!validation.isValid) {
        const errorMessage = `Validation failed: ${validation.errors.join(', ')}`;
        if (validation.warnings.length > 0) {
          console.warn('Strategy validation warnings:', validation.warnings);
        }
        throw new Error(errorMessage);
      }

      // Show warnings to user (non-blocking)
      if (validation.warnings.length > 0) {
        console.warn('Strategy creation warnings:', validation.warnings);
      }

      // Extract strategy-level context
      const strategyIndex = strategyData.index;
      const expiryType = strategyData.config.expiryType;

      if (!strategyIndex || !expiryType) {
        throw new Error('Strategy index and expiry type are required');
      }

      // Transform frontend data to backend format with strategy context
      const backendPayload = strategyTransformationService.createApiPayload(
        basketId,
        strategyData,
        strategyIndex,
        expiryType
      );

      // Make API call to create strategy
      const response = await apiClient.post<ApiResponse<any>>(
        withQs(`/baskets/${basketId}/strategies`),
        backendPayload,
        undefined,
        API_TYPE
      );

      if (!response.success) {
        throw new Error(response.message || 'Failed to create strategy');
      }

      return {
        success: true,
        data: response.data,
        message: response.message || 'Strategy created successfully',
        warnings: validation.warnings.length > 0 ? validation.warnings : undefined
      };
    } catch (error: any) {
      // Enhanced error handling with transformation details
      if (error.message?.includes('Validation failed')) {
        throw new Error(error.message);
      }

      const apiError = error.response?.data?.message || error.message;
      throw new Error(`Failed to create strategy: ${apiError}`);
    }
  }

  /**
   * Get all strategies for a specific basket
   */
  async getBasketStrategies(basketId: string): Promise<StrategyMetadata[]> {
    try {
      const response = await apiClient.get<ApiResponse<any[]>>(
        withQs(`/baskets/${basketId}/strategies`),
        undefined,
        API_TYPE
      );

      if (!response.success || !response.data) {
        throw new Error(response.message || 'Failed to fetch basket strategies');
      }

      // Transform backend strategy fields to frontend format and sort by
      // (displayOrder, createdAt). Legacy rows without displayOrder fall to
      // the bottom in stable creation-date order.
      return response.data
        .map(transformStrategyFields)
        .sort((a, b) => {
          const ao = a.displayOrder ?? Number.POSITIVE_INFINITY;
          const bo = b.displayOrder ?? Number.POSITIVE_INFINITY;
          if (ao !== bo) return ao - bo;
          return (a.createdAt || '').localeCompare(b.createdAt || '');
        });
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch basket strategies');
    }
  }

  /**
   * Get detailed information about a specific strategy
   */
  async getStrategy(strategyId: string): Promise<StrategyWithLegs> {
    try {
      const response = await apiClient.get<ApiResponse<any>>(
        withQs(`/strategies/${strategyId}`),
        undefined,
        API_TYPE
      );

      if (!response.success || !response.data) {
        throw new Error(response.message || 'Failed to fetch strategy details');
      }

      // Use full transformation for editing mode - properly transforms legs from snake_case to camelCase
      const basketId = response.data.basket_id || 'unknown-basket';
      const transformed = strategyTransformationService.transformToFrontend(response.data, basketId);

      return {
        ...response.data,  // Preserve original structure
        ...transformed,    // Add properly transformed fields including config
        legsArray: transformed.legs  // Properly transformed legs for editing
      };
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch strategy details');
    }
  }

  /**
   * Update an existing strategy
   */
  async updateStrategy(strategyId: string, updates: Partial<BackendStrategyData>): Promise<any> {
    try {
      const response = await apiClient.put<ApiResponse<any>>(
        withQs(`/strategies/${strategyId}`),
        updates,
        undefined,
        API_TYPE
      );

      if (!response.success) {
        throw new Error(response.message || 'Failed to update strategy');
      }

      // Return the response info (backend doesn't return data field for updates)
      return {
        success: true,
        strategyId: (response as any).strategy_id || strategyId, // Use provided strategyId as fallback
        message: response.message
      };
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to update strategy');
    }
  }

  /**
   * Delete a strategy
   */
  async deleteStrategy(strategyId: string): Promise<void> {
    try {
      const response = await apiClient.delete<ApiResponse>(
        withQs(`/strategies/${strategyId}`),
        undefined,
        API_TYPE
      );

      if (!response.success) {
        throw new Error(response.message || 'Failed to delete strategy');
      }
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to delete strategy');
    }
  }

  /**
   * Delete all strategies in a basket (bulk delete)
   */
  async deleteAllBasketStrategies(basketId: string): Promise<{
    success: boolean;
    deletedCount: number;
    failedCount: number;
    failedStrategyIds?: string[];
    message: string;
  }> {
    try {
      const response = await apiClient.delete<ApiResponse<{
        deleted_count: number;
        failed_count: number;
        failed_strategy_ids?: string[];
        message: string;
      }>>(withQs(`/baskets/${basketId}/strategies/bulk-delete`), undefined, API_TYPE);

      if (!response.success) {
        throw new Error(response.message || 'Failed to delete all strategies');
      }

      return {
        success: true,
        deletedCount: response.data?.deleted_count || 0,
        failedCount: response.data?.failed_count || 0,
        failedStrategyIds: response.data?.failed_strategy_ids,
        message: response.data?.message || response.message || 'All strategies deleted successfully'
      };
    } catch (error: any) {
      const errorData = error.response?.data;

      // Handle partial success scenarios (207 Multi-Status)
      if (error.response?.status === 207 && errorData) {
        return {
          success: true, // Partial success is still considered success
          deletedCount: errorData.deleted_count || 0,
          failedCount: errorData.failed_count || 0,
          failedStrategyIds: errorData.failed_strategy_ids,
          message: errorData.message || 'Bulk deletion completed with some failures'
        };
      }

      throw new Error(errorData?.message || error.message || 'Failed to delete all strategies');
    }
  }

  /**
   * Get strategy execution status with performance metrics
   */
  async getStrategyStatus(strategyId: string): Promise<StrategyExecutionStatus> {
    try {
      const response = await apiClient.get<ApiResponse<StrategyExecutionStatus>>(
        `/strategies/${strategyId}/status`,
        undefined,
        API_TYPE
      );

      if (!response.success || !response.data) {
        throw new Error(response.message || 'Failed to fetch strategy status');
      }

      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch strategy status');
    }
  }

  /**
   * Start strategy execution
   */
  async startStrategy(strategyId: string): Promise<void> {
    try {
      const response = await apiClient.post<ApiResponse>(
        `/strategies/${strategyId}/start`,
        undefined,
        undefined,
        API_TYPE
      );

      if (!response.success) {
        throw new Error(response.message || 'Failed to start strategy');
      }
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to start strategy');
    }
  }

  /**
   * Pause strategy execution
   */
  async pauseStrategy(strategyId: string): Promise<void> {
    try {
      const response = await apiClient.post<ApiResponse>(
        `/strategies/${strategyId}/pause`,
        undefined,
        undefined,
        API_TYPE
      );

      if (!response.success) {
        throw new Error(response.message || 'Failed to pause strategy');
      }
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to pause strategy');
    }
  }

  /**
   * Stop strategy execution
   */
  async stopStrategy(strategyId: string): Promise<void> {
    try {
      const response = await apiClient.post<ApiResponse>(
        `/strategies/${strategyId}/stop`,
        undefined,
        undefined,
        API_TYPE
      );

      if (!response.success) {
        throw new Error(response.message || 'Failed to stop strategy');
      }
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to stop strategy');
    }
  }

  /**
   * Get strategy performance analytics
   */
  async getStrategyAnalytics(strategyId: string, timeframe: 'day' | 'week' | 'month' = 'week'): Promise<{
    executions: any[];
    performance: {
      totalReturn: number;
      successRate: number;
      averageReturn: number;
      maxDrawdown: number;
      sharpeRatio: number;
    };
    charts: {
      pnlChart: { date: string; value: number }[];
      winRateChart: { date: string; rate: number }[];
    };
  }> {
    try {
      const response = await apiClient.get<ApiResponse<any>>(
        `/strategies/${strategyId}/analytics?timeframe=${timeframe}`,
        undefined,
        API_TYPE
      );

      if (!response.success || !response.data) {
        throw new Error(response.message || 'Failed to fetch strategy analytics');
      }

      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch strategy analytics');
    }
  }

  /**
   * Clone an existing strategy to a different basket
   */
  async cloneStrategy(strategyId: string, targetBasketId: string, newName?: string): Promise<any> {
    try {
      const response = await apiClient.post<ApiResponse<any>>(
        withQs(`/strategies/${strategyId}/clone`),
        {
          target_basket_id: targetBasketId,
          new_name: newName
        },
        undefined,
        API_TYPE
      );

      if (!response.success || !response.data) {
        throw new Error(response.message || 'Failed to clone strategy');
      }

      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to clone strategy');
    }
  }

  /**
   * Bulk-copy strategies from any source basket(s) into a single target basket.
   * Per-strategy outcomes returned in succeeded[] / failed[] — partial success
   * is the contract.
   */
  async bulkCopyStrategies(
    targetBasketId: string,
    sourceStrategyIds: string[],
  ): Promise<{
    target_basket_id: string;
    succeeded: Array<{ source_strategy_id: string; new_strategy_id: string; strategy_name: string }>;
    failed: Array<{ source_strategy_id: string; error: string; message: string }>;
  }> {
    if (!targetBasketId) {
      throw new Error('targetBasketId is required');
    }
    if (!sourceStrategyIds.length) {
      throw new Error('At least one source strategy must be selected');
    }
    try {
      const response = await apiClient.post<ApiResponse<any>>(
        withQs(`/baskets/${targetBasketId}/copy-strategies`),
        { source_strategy_ids: sourceStrategyIds },
        undefined,
        API_TYPE,
      );
      if (!response.data) {
        throw new Error(response.message || 'Failed to copy strategies');
      }
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        'Failed to copy strategies',
      );
    }
  }

  /**
   * Get strategy execution history
   */
  async getStrategyHistory(strategyId: string, limit: number = 50): Promise<{
    executions: Array<{
      executionId: string;
      executionDate: string;
      executionType: 'ENTRY' | 'EXIT';
      status: 'SUCCESS' | 'FAILED' | 'PARTIAL';
      totalPnL: number;
      legs: Array<{
        legId: string;
        symbol: string;
        action: string;
        quantity: number;
        price: number;
        pnl: number;
      }>;
    }>;
    summary: {
      totalExecutions: number;
      successfulExecutions: number;
      totalPnL: number;
    };
  }> {
    try {
      const response = await apiClient.get<ApiResponse<any>>(
        `/strategies/${strategyId}/history?limit=${limit}`,
        undefined,
        API_TYPE
      );

      if (!response.success || !response.data) {
        throw new Error(response.message || 'Failed to fetch strategy history');
      }

      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch strategy history');
    }
  }

  /**
   * Validate strategy configuration before creation
   */
  validateStrategyData(strategyData: FrontendStrategyData): { isValid: boolean; errors: string[] } {
    return strategyTransformationService.validateFrontendData(strategyData);
  }

  /**
   * Get strategy templates/presets
   */
  async getStrategyTemplates(): Promise<Array<{
    templateId: string;
    name: string;
    description: string;
    category: 'BULLISH' | 'BEARISH' | 'NEUTRAL' | 'VOLATILITY';
    complexity: 'BEGINNER' | 'INTERMEDIATE' | 'ADVANCED';
    defaultConfig: Partial<FrontendStrategyData>;
  }>> {
    try {
      const response = await apiClient.get<ApiResponse<any>>(
        '/strategy-templates',
        undefined,
        API_TYPE
      );

      if (!response.success || !response.data) {
        throw new Error(response.message || 'Failed to fetch strategy templates');
      }

      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch strategy templates');
    }
  }

  /**
   * Update strategy status (enable/disable)
   */
  async updateStrategyStatus(strategyId: string, status: 'ACTIVE' | 'PAUSED' | 'COMPLETED'): Promise<any> {
    try {
      const response = await apiClient.put<ApiResponse<any>>(
        withQs(`/strategies/${strategyId}`),
        { status },
        undefined,
        API_TYPE
      );

      if (!response.success) {
        throw new Error(response.message || 'Failed to update strategy status');
      }

      return {
        success: true,
        strategyId: (response as any).strategy_id || strategyId,
        message: response.message || 'Strategy status updated successfully'
      };
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to update strategy status');
    }
  }

  /**
   * Reorder strategies within a basket via the generic strategies/actions
   * dispatcher. Caller must supply the full set of strategy_ids in the
   * desired order — server enforces set-equality.
   */
  async reorderStrategies(basketId: string, strategyIds: string[]): Promise<StrategyMetadata[]> {
    try {
      const response = await apiClient.post<ApiResponse<any[]>>(
        withQs(`/baskets/${basketId}/strategies/actions`),
        { action: 'reorder', strategy_ids: strategyIds },
        undefined,
        API_TYPE
      );

      if (!response.success || !response.data) {
        throw new Error(response.message || 'Failed to reorder strategies');
      }

      return response.data.map(transformStrategyFields);
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to reorder strategies');
    }
  }
}

const strategyService = new StrategyService();
export default strategyService;
export { StrategyService };
export type { StrategyExecutionStatus, StrategyCreationResponse };